import React, { useState } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  List,
  ListItem,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import api from "../../api/axiosConfig";
import { endpoints } from "../../api/endpoints";
import { Header } from "../../components/Header/Headers";
import { colors } from "../../theme/colors";
import { getApiErrorMessage } from "../../utils/apiError";

// const commonPasswordTerms = [
//   "password",
//   "admin",
//   "welcome",
//   "qwerty",
//   "letmein",
//   "123456",
//   "abc123",
//   "changeme",
// ];

const getPasswordValidationMessage = ({
  currentPassword,
  newPassword,
  confirmPassword,
}: {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}) => {
  if (!currentPassword || !newPassword || !confirmPassword) {
    return "Please fill in all fields.";
  }

  if (newPassword.length < 8) {
    return "New password must be at least 8 characters long.";
  }

  if (!/[A-Z]/.test(newPassword)) {
    return "New password must include at least one uppercase letter.";
  }

  if (!/[a-z]/.test(newPassword)) {
    return "New password must include at least one lowercase letter.";
  }

  if (!/\d/.test(newPassword)) {
    return "New password must include at least one number.";
  }

  if (!/[^A-Za-z0-9]/.test(newPassword)) {
    return "New password must include at least one special character.";
  }

  // if (commonPasswordTerms.some((term) => newPassword.toLowerCase().includes(term))) {
  //   return "New password should not contain common words or weak password patterns.";
  // }

  if (newPassword === currentPassword) {
    return "New password must be different from your current password.";
  }

  if (newPassword !== confirmPassword) {
    return "New password and confirm password do not match.";
  }

  return null;
};

const ChangePasswordPage: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    const validationMessage = getPasswordValidationMessage({
      currentPassword,
      newPassword,
      confirmPassword,
    });

    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    setIsSubmitting(true);

    try {
      await api.post(endpoints.auth.changePassword, {
        current_password: currentPassword,
        new_password: newPassword,
      });

      setSuccess("Password changed successfully!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      setError(
        getApiErrorMessage(err, "Failed to change password. Please try again."),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setError(null);
    setSuccess(null);
  };

  return (
    <>
      <Header
        title="Change Password"
        breadcrumb="Admin Console / Change Password"
      />
      <Container maxWidth="md" sx={{ py: 4, ml: { xs: 0, md: 2 } }}>
        {/* Change Password Form */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 4 },
            borderRadius: 1.5,
            border: "1px solid",
            borderColor: "divider",
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 1.5,
                backgroundColor: "#E6F2EA",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LockIcon sx={{ color: "#2E7D32" }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                component="h1"
                sx={{
                  fontWeight: 700,
                  lineHeight: "32px",
                  fontSize: "20px",
                  fontFamily: "Inter, sans-serif",
                  color: colors.black,
                }}
              >
                Change Password
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  mt: 0.3,
                  fontSize: "14px",
                  fontFamily: "Inter, sans-serif",
                  color: colors.darkGray,
                }}
              >
                Update your password to keep your account secure
              </Typography>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              label="Current Password"
              margin="normal"
              placeholder="Current Password *"
              type={showCurrentPassword ? "text" : "password"}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              disabled={isSubmitting}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowCurrentPassword(!showCurrentPassword)
                        }
                        edge="end"
                        disabled={isSubmitting}
                      >
                        {showCurrentPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              fullWidth
              label="New Password"
              margin="normal"
              placeholder="New Password *"
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              helperText="Use at least 8 characters with uppercase, lowercase, number, and special character."
              disabled={isSubmitting}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                        disabled={isSubmitting}
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              fullWidth
              label="Confirm New Password"
              margin="normal"
              placeholder="Confirm New Password *"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={isSubmitting}
              sx={{ mb: 1 }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                        disabled={isSubmitting}
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 3,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={isSubmitting}
                sx={{
                  textTransform: "none",
                  borderColor: "#A5D6A7",
                  color: "#2E7D32",
                  px: 3,
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  textTransform: "none",
                  backgroundColor: "#2E7D32",
                  px: 3,
                  "&:hover": {
                    backgroundColor: "#256B29",
                  },
                }}
              >
                {isSubmitting ? "Changing..." : "Change Password"}
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Password Security Tips */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 3.5 },
            borderRadius: 1.5,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: "20px",
              fontFamily: "Inter, sans-serif",
              color: colors.black,
              mb: 2.5,
            }}
          >
            Password Security Tips
          </Typography>
          <List disablePadding>
            <ListItem disableGutters sx={{ py: 0.8, px: 2 }}>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  fontSize: "14px",
                  fontFamily: "Inter, sans-serif",
                  color: colors.darkGray,
                  fontWeight: 400,
                }}
              >
                Use a strong password with at least 8 characters
              </Typography>
            </ListItem>
            <ListItem disableGutters sx={{ py: 0.8, px: 2 }}>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  fontSize: "14px",
                  fontFamily: "Inter, sans-serif",
                  color: colors.darkGray,
                  fontWeight: 400,
                }}
              >
                Include uppercase and lowercase letters, numbers, and special
                characters
              </Typography>
            </ListItem>
            <ListItem disableGutters sx={{ py: 0.8, px: 2 }}>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  fontSize: "14px",
                  fontFamily: "Inter, sans-serif",
                  color: colors.darkGray,
                  fontWeight: 400,
                }}
              >
                Don&apos;t use common words or personal information
              </Typography>
            </ListItem>
            <ListItem disableGutters sx={{ py: 0.8, px: 2 }}>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  fontSize: "14px",
                  fontFamily: "Inter, sans-serif",
                  color: colors.darkGray,
                  fontWeight: 400,
                }}
              >
                Change your password regularly and don&apos;t reuse old
                passwords
              </Typography>
            </ListItem>
          </List>
        </Paper>
      </Container>
    </>
  );
};

export default ChangePasswordPage;
